import { EnumButtonName } from '@calm/evexia-analytics';
import { useAudio } from '@rhg/hooks/use-audio';
import playerMessages from '@rhg/lang/messages/player';
import {
    IconButton,
    IconButtonProps,
    SxProps,
    Theme,
} from '@rhg/libs/material';
import { Forward15, SkipForward } from '@rhg/libs/material/icons';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

export interface NextButtonProps {
    /** True for playlist content: button skips to next track instead of forwarding 15s. */
    isPlaylist?: boolean;
    onNextTrack?: () => void;
    sx?: SxProps<Theme>;
    size?: IconButtonProps['size'];
}

export function NextButton({
    isPlaylist = false,
    onNextTrack,
    sx,
    size,
}: NextButtonProps) {
    const { formatMessage } = useIntl();
    // Read the playhead imperatively at click time from the (low-frequency)
    // media element rather than subscribing to the ~4Hz timing — this button
    // never needs to re-render as time advances. EVE-12668.
    const {
        controls: { setTime },
        mediaEl,
    } = useAudio();

    const onForward = useCallback(() => {
        const duration = mediaEl?.duration ?? 0;
        setTime(
            Math.min(
                (mediaEl?.currentTime ?? 0) + 15,
                // Fall back to an effectively unbounded cap when duration is
                // not yet known (NaN/0) so the seek isn't clamped to 0.
                duration || Number.MAX_SAFE_INTEGER
            )
        );
    }, [setTime, mediaEl]);

    return (
        <IconButton
            trackButton={
                isPlaylist
                    ? EnumButtonName.NextTrack
                    : EnumButtonName.ForwardFewSeconds
            }
            aria-label={
                isPlaylist
                    ? formatMessage(playerMessages['player.next_track'])
                    : formatMessage(playerMessages['player.forward_15'])
            }
            onClick={isPlaylist ? onNextTrack : onForward}
            size={size}
            sx={sx}
        >
            {isPlaylist ? <SkipForward /> : <Forward15 />}
        </IconButton>
    );
}
